import React, { useState, useEffect } from 'react';
import { TechnologyTable } from './TechnologyTable';
import { ConfigureVendorsButton } from './ConfigureVendorsButton';
import { ConfigureVendorsModal } from './ConfigureVendorsModal';
import { HelpButton } from '../common/HelpButton';
import { useCustomer } from '../../context/CustomerContext';
import { customerApi } from '../../services/api';
import { toast } from 'react-toastify';

interface TechnologyTabProps {
    onShowHelp?: () => void;
}

// Define interfaces (technology_group_id and technology_id for POST one-item contract)
interface TechnologyDomain {
    id: string;
    name: string;
    technology_group_id?: string;
}

interface Technology {
    id: string;
    name: string;
    domainId: string;
    status: 'In Production' | 'No Usage' | 'Deploying' | 'Optimizing';
    notes?: string;
    technology_id?: string;
}

interface StrategicVendor {
    id: string;
    name: string;
    inUse: boolean;
    technology_id?: string;
}

export const TechnologyTab: React.FC<TechnologyTabProps> = ({ onShowHelp }) => {
    const { customerData, updateCustomerData } = useCustomer();
    const [domains, setDomains] = useState<TechnologyDomain[]>([]);
    const [technologies, setTechnologies] = useState<Technology[]>([]);
    const [vendors, setVendors] = useState<StrategicVendor[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Build domains, technologies, and vendors only from customer technology API (no static_technologies or localStorage)
    useEffect(() => {
        const tech = customerData?.technology;
        if (!tech) {
            setDomains([]);
            setTechnologies([]);
            setVendors([]);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const groups = tech.technology_groups || [];
            const validStatuses: Technology['status'][] = ['In Production', 'Deploying', 'Optimizing', 'No Usage'];

            const domainsData: TechnologyDomain[] = groups.map((g, i) => ({
                id: `domain-${i + 1}`,
                name: g.group ?? '',
                technology_group_id: g.technology_group_id
            }));

            const technologiesData: Technology[] = [];
            groups.forEach((g, gi) => {
                const domainId = `domain-${gi + 1}`;
                (g.technologies || []).forEach((t, ti) => {
                    const status = t.status && validStatuses.includes(t.status as Technology['status'])
                        ? (t.status as Technology['status'])
                        : 'No Usage';
                    technologiesData.push({
                        id: `tech-${gi}-${ti}`,
                        name: t.name ?? '',
                        domainId,
                        status,
                        notes: t.notes,
                        technology_id: t.technology_id ?? undefined
                    });
                });
            });

            const vendorsData: StrategicVendor[] = (tech.strategic_vendors || []).map((v, i) => ({
                id: `vendor-${i + 1}`,
                name: v.vendor ?? '',
                inUse: v.status === 'Yes',
                technology_id: v.technology_id
            }));

            setDomains(domainsData);
            setTechnologies(technologiesData);
            setVendors(vendorsData);
        } catch (error) {
            console.error('Error building technology data:', error);
            setDomains([]);
            setTechnologies([]);
            setVendors([]);
        } finally {
            setIsLoading(false);
        }
    }, [customerData?.technology]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleSaveVendors = async (updatedVendors: StrategicVendor[]) => {
        if (!customerData?.customer_id) return;
        const customerId = customerData.customer_id;
        const changedVendors = updatedVendors.filter((uv) => {
            const prev = vendors.find((v) => v.id === uv.id || v.name === uv.name);
            return prev != null && prev.inUse !== uv.inUse;
        });
        try {
            setVendors(updatedVendors);
            for (const vendor of changedVendors) {
                const technology_id = vendor.technology_id;
                if (!technology_id) continue;
                await customerApi.createOrUpdateTechnologyItem(customerId, {
                    technology_group_id: 'STRATEGIC_VENDOR',
                    label: vendor.name,
                    technology_id,
                    data: { name: vendor.name, status: vendor.inUse ? 'Yes' : 'No', notes: '' }
                });
            }
            const strategic_vendors = updatedVendors.map((v) => ({
                vendor: v.name,
                status: v.inUse ? 'Yes' : 'No',
                ...(v.technology_id != null && { technology_id: v.technology_id })
            }));
            updateCustomerData({
                technology: {
                    ...customerData?.technology,
                    strategic_vendors
                }
            });
            toast.success('Strategic vendors updated successfully!');
        } catch (error) {
            console.error('Error updating strategic vendors:', error);
            toast.error('Failed to update strategic vendors.');
            setVendors(vendors);
        }
    };

    return (
        <div className='m-0 w-full px-4'>
            <div className="flex items-center justify-between mb-6">
                <div className='flex gap-3 items-center'>
                    <h1 className="text-2xl font-bold italic text-[#0F2A4F]">Key Technologies</h1>
                    {onShowHelp && <HelpButton onClick={onShowHelp} />}
                </div>
                <ConfigureVendorsButton onClick={handleOpenModal} />
            </div>

            <div className="">
                <div className="flex flex-col mb-8">
                    {isLoading ? (
                        <div className="flex justify-center py-10 items-center h-full">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#002D72]"></div>
                        </div>
                    ) : (
                        <TechnologyTable 
                            domains={domains} 
                            technologies={technologies} 
                            customerId={customerData?.customer_id}
                            keyTechnology={customerData?.technology?.key_technology}
                        />
                    )}
                </div>

                <ConfigureVendorsModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    vendors={vendors}
                    onSave={handleSaveVendors}
                />
            </div>
        </div>
    );
};
